import { Colors, Heading, Icon, Text } from '@amelio/ui-library';

import { DesktopRevieweeCard } from './components/DesktopRevieweeCard';
import { MobileRevieweeCard } from './components/MobileRevieweeCard';
import { SurveyAnswerDescription } from './components/SurveyAnswerDescription';

import type { TranslationKey } from '@/i18n-key';
import type { User } from '@/types/directory/User';

import { FeatureItem } from '@/components/FeatureItem/FeatureItem';
import {
    PeerReviewToRevieweeFrIllustration,
    PeerReviewToRevieweeIllustration,
} from '@/components/Illustrations/Illustration';
import { useAmelioTranslations } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import i18n from '@/plugins/i18n';
import { getFullName, getProfilePictureUrl } from '@/utils/user';

const REVIEWEE_CARD_BORDER_COLOR = `${Colors.PRIMARY}4D`;
// Hung from the reviewee card's left edge, so the tip keeps touching it wherever the card sits.
// At 420x170 the arrowhead lands ~410px across and ~51px down the SVG: ending the SVG 10px past
// the card's left edge puts the tip on the border, ~24px below the card's top.
const DECORATIVE_ARROW_RIGHT = 'calc(100% - 10px)';
const DECORATIVE_ARROW_TOP = '-27px';
const DECORATIVE_ARROW_ROTATION = 'rotate(1.473deg)';

export interface SurveyAnswerHeaderProps {
    reviewee?: User;
    isSelfEvaluation?: boolean;
    isAnonymous?: boolean;
    /** Said instead of the usual promise when the person evaluated launched the 360 and reads its results. */
    revieweeSees?: TranslationKey;
}

export const SurveyAnswerHeader = ({
    reviewee,
    isSelfEvaluation = false,
    isAnonymous = false,
    revieweeSees,
}: SurveyAnswerHeaderProps) => {
    const { t } = useAmelioTranslations();
    const { getJobTitle, getManagerLabel } = useCompanyInformation();
    const isFrench = i18n.language.startsWith('fr');

    const revieweeFullName = getFullName(reviewee);
    const revieweeJobTitle = getJobTitle(reviewee?.jobTitleId || '');
    const revieweeProfilePictureUrl = getProfilePictureUrl(reviewee?.profilePictureUrl || '');
    const managerLabel = getManagerLabel();
    const revieweeSeesText = revieweeSees && t(revieweeSees);

    const surveyAnswerHeading = isSelfEvaluation
        ? t('answer.surveyAnswer.selfEvaluationTitle')
        : t('answer.surveyAnswer.featureItem.title');
    const selfEvaluationDescription = t('answer.surveyAnswer.selfEvaluationDescription', {
        managerLabel,
    });
    const selfEvaluationPillLabel = t('answer.surveyAnswer.selfEvaluationPill');
    const reviewedColleaguePillLabel = t('answer.surveyAnswer.reviewedCollegue');

    return (
        <div className={'relative'}>
            {/* Mobile: Unified compact card (< md) */}
            <div
                className={
                    'hide-from-md rounded-2xl border border-white bg-white/45 p-4 backdrop-blur-[2px]'
                }
            >
                <div className={'mb-4 flex items-start gap-3'}>
                    <div
                        className={
                            'bg-primary-50 flex h-10 w-10 shrink-0 items-center justify-center rounded-full'
                        }
                    >
                        <Icon
                            name={'arrow-refresh-2'}
                            size={20}
                            color={Colors.PRIMARY}
                        />
                    </div>

                    <div className={'min-w-0 flex-1'}>
                        <Heading
                            level={3}
                            weight={'medium'}
                            color={Colors.BASE}
                            className={'text-lg leading-6'}
                        >
                            {surveyAnswerHeading}
                        </Heading>

                        <Text
                            size={'small'}
                            className={'mt-1 text-xs leading-relaxed'}
                        >
                            <SurveyAnswerDescription
                                isSelfEvaluation={isSelfEvaluation}
                                selfEvaluationDescription={selfEvaluationDescription}
                                managerLabel={managerLabel}
                                isAnonymous={isAnonymous}
                                revieweeSees={revieweeSeesText}
                            />
                        </Text>
                    </div>
                </div>

                <MobileRevieweeCard
                    isSelfEvaluation={isSelfEvaluation}
                    revieweeFullName={revieweeFullName}
                    revieweeJobTitle={revieweeJobTitle}
                    revieweeProfilePictureUrl={revieweeProfilePictureUrl}
                    selfEvaluationPillLabel={selfEvaluationPillLabel}
                    reviewedColleaguePillLabel={reviewedColleaguePillLabel}
                    revieweeCardBorderColor={REVIEWEE_CARD_BORDER_COLOR}
                />
            </div>

            {/* Desktop: Separate cards with responsive layout (≥ md) */}
            <div className={'show-from-md flex-col items-start gap-6'}>
                <div className={'relative w-full max-w-[503px]'}>
                    <FeatureItem
                        className={'max-w-[503px]'}
                        variant={'compact'}
                        icon={'arrow-refresh-2'}
                        heading={surveyAnswerHeading}
                        description={
                            <SurveyAnswerDescription
                                isSelfEvaluation={isSelfEvaluation}
                                selfEvaluationDescription={selfEvaluationDescription}
                                managerLabel={managerLabel}
                                isAnonymous={isAnonymous}
                                revieweeSees={revieweeSeesText}
                            />
                        }
                    />
                </div>

                {/* Pinned to the container's right edge */}
                <div className={'relative self-end'}>
                    {/* Decorative Arrow — visible only on xl+ (1200px) */}
                    {!isSelfEvaluation && (
                        <div
                            className={'show-from-xl pointer-events-none absolute z-10'}
                            style={{
                                right: DECORATIVE_ARROW_RIGHT,
                                top: DECORATIVE_ARROW_TOP,
                                transform: DECORATIVE_ARROW_ROTATION,
                            }}
                        >
                            {isFrench ? (
                                <PeerReviewToRevieweeFrIllustration
                                    className={'h-[170px] w-[420px]'}
                                />
                            ) : (
                                <PeerReviewToRevieweeIllustration
                                    className={'h-[170px] w-[420px]'}
                                />
                            )}
                        </div>
                    )}

                    <DesktopRevieweeCard
                        isSelfEvaluation={isSelfEvaluation}
                        reviewee={reviewee}
                        revieweeFullName={revieweeFullName}
                        revieweeJobTitle={revieweeJobTitle}
                        revieweeProfilePictureUrl={revieweeProfilePictureUrl}
                        selfEvaluationPillLabel={selfEvaluationPillLabel}
                        reviewedColleaguePillLabel={reviewedColleaguePillLabel}
                    />
                </div>
            </div>
        </div>
    );
};
